// AccommodationsPage — /accommodations, the RESTRICTED RRA staff console
// (phase 4; docs/design/rra-workflow.md "Adversarial-review outcome").
//
// The ONLY staff surface for accommodation cases (review fix #2 — nothing
// about them rides the generic Requests screen). Access is enforced
// server-side by the RRA_STAFF allowlist; a 403 renders the restricted notice.
//
// Queue (requester · subject · scope · status · freeze · age) + a detail
// modal with the working panels: Intake (send/resend/staff-fill/view),
// Apparent-gate, Verification (create/send/cancel/view response), Decision
// (approve / deny / more-info / withdraw), and Freeze status. A prominent
// "Test request" affordance on create lets staff type any email and drive the
// whole flow to a test inbox — the API pins a test case's emails to the
// RRA_TEST_EMAIL_ALLOWLIST at the send layer.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  addRraRequest,
  approveRraItem,
  attachRraCase,
  cancelRraVerification,
  confirmRraPhone,
  confirmRraWritingAddress,
  createRraCase,
  createRraVerification,
  deleteRraTestCase,
  denyRraItem,
  detachRraCase,
  getRraCase,
  getRraHouseholdAnimals,
  listRraCases,
  markRraFreezeActionApplied,
  recordRraDeclineCheck,
  recordRraGate,
  recordRraOralVerification,
  removeRraCallNote,
  requestMoreInfoRraItem,
  resumeRraFrontline,
  revokeRraBuildApproval,
  rraErrorMessage,
  rraLetterDownloadUrl,
  rraStaffDocumentUrl,
  rraVerifierCopyBlob,
  searchRraPeople,
  sendRraIntake,
  sendRraNoNote,
  sendRraVerification,
  setRraItemAnimals,
  staffFillRraIntake,
  triggerRraFrontline,
  withdrawRraCase,
  withdrawRraItem,
  withdrawRraPermission,
  ApiError,
  type CreateRraCaseBody,
  type RraApproveAnimal,
  type RraCaseView,
  type RraCasesListResponse,
  type RraChannel,
  type RraItemView,
  type RraPersonHit,
  type RraScope,
  type RraVerificationView,
  type RraWithdrawalChannel,
} from '@/lib/rraStaffClient';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';
import {
  formVoice,
  requestDraftFromItem,
  staffItemFields,
  type RequestDraft,
} from '@/lib/rraRequestDraft';
import { RequestStatementFields } from '@/components/RraRequestFields';
// RRA v2.1 (W1)
import {
  GATE,
  SPOKEN_DEFINITION,
  WHO_FOR,
  disabilityQuestion,
  needQuestion,
  positionQuestion,
  type RraCopySubject,
} from '@/lib/rraCopy';
import { HelpBox } from '@/components/rra/HelpBox';
import { FREEZE_TASKS_INTRO, freezeActionLabel, freezeActionSummary } from '@/lib/rraFreezeActions';
import {
  APPFOLIO_TASK_PENDING,
  NOTHING_NEEDS_ACTION,
  QUEUE_VIEW_LABEL,
  actionCount,
  hasPendingAppfolioTask,
  visibleCases,
  type QueueView,
} from '@/lib/rraCaseList';
import { AddRequestPanel } from '@/components/rra/AddRequestPanel';
import { ApprovePicker, type ApprovePickerMode } from '@/components/rra/ApprovePicker';
import { RequestRows } from '@/components/rra/RequestRows';
import {
  addendumLine,
  approveOutcome,
  approvedEffectLine,
  canChangeAnimals,
  canRunApproveAgain,
  changeAnimalsOutcome,
  needsAnimalPicker,
  runApproveAgainNeedsPicker,
  type RosterLoad,
} from '@/lib/rraApprovePicker';
import {
  blankRequestRow,
  coversLine,
  gateScopeFor,
  isAnimalItem,
  isOpenItem,
  isUnanswered,
  itemOrdinalLabel,
  itemTitle,
  itemsFromRows,
  requestBullets,
  requestVoice,
  rowsValid,
  unansweredItems,
  type RequestRow,
} from '@/lib/rraRequests';
import { SubjectFields, type SubjectValue } from '@/components/rra/SubjectFields';
import { askRraPermission, rraCaseRequestPdf, type RraSubjectView } from '@/lib/rraStaffClient';
import {
  emptyGateDraft,
  gateDraftFromEvaluation,
  gateDraftToBody,
  gateEvaluationLines,
  gateRevisedNotice,
  gateRevisionNumber,
  validateGateDraft,
  type GateDraft,
} from '@/lib/rraGateDraft';
import { AttentionPanel, type RraRunFn } from '@/components/rra/AttentionPanel';
import { DeclineCheckForm } from '@/components/rra/DeclineCheckForm';
import { GateFields } from '@/components/rra/GateFields';
import {
  WithdrawPermissionForm,
  formatCalendarDate,
  withdrawalNotice,
} from '@/components/rra/WithdrawPermissionForm';
// RRA v2.1 (W3)
import {
  answerBody,
  answerError,
  answeredNo,
  BLANK_ANSWERS,
  canRecordReply,
  canSend,
  contactLine,
  contactNotice,
  DOCUMENT_KIND_WORD,
  gapOfItems,
  gapOfVerification,
  isOpenAsk,
  itemsOfVerification,
  needObjectForItems,
  RECEIVED_AFTER_WITHDRAWAL,
  receivedAtFromDate,
  requesterNotesLine,
  responseLine,
  subjectNameOf,
  verificationPills,
  type AnswerSelection,
} from '@/lib/rraVerificationUi';
import { AddDocumentForm } from '@/components/rra/AddDocumentForm';
import { FrontlineTimeline } from '@/components/rra/FrontlineTimeline';
import { ProngAnswer } from '@/components/rra/ProngAnswer';
import { RecordReplyForm } from '@/components/rra/RecordReplyForm';

type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral';

const CASE_STATUS_META: Record<string, { label: string; tone: PillTone }> = {
  logged: { label: 'Logged', tone: 'neutral' },
  intake_sent: { label: 'Intake sent', tone: 'info' },
  intake_complete: { label: 'Intake in', tone: 'info' },
  gate_review: { label: 'Gate review', tone: 'warning' },
  verification_pending: { label: 'Verifying', tone: 'info' },
  verification_received: { label: 'Verification in', tone: 'success' },
  evaluation: { label: 'Evaluate', tone: 'warning' },
  more_info_requested: { label: 'More info', tone: 'warning' },
  denial_approval: { label: 'Denial approval', tone: 'warning' },
  dormant: { label: 'Dormant', tone: 'neutral' },
  decided: { label: 'Decided', tone: 'success' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

const ITEM_STATUS_META: Record<string, { label: string; tone: PillTone }> = {
  ...CASE_STATUS_META,
  open: { label: 'Open', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'success' },
  denied: { label: 'Denied', tone: 'danger' },
};

const SCOPE_LABEL: Record<RraScope, string> = {
  esa_animal: 'ESA',
  service_animal: 'Service animal',
  other_accommodation: 'Accommodation',
  modification: 'Modification',
};

const CHANNEL_OPTIONS: Array<{ value: RraChannel; label: string }> = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'in_person', label: 'In person' },
  { value: 'mail', label: 'Mail' },
  { value: 'other', label: 'Other' },
];

const WITHDRAW_CHANNELS: Array<{ value: RraWithdrawalChannel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'in_person', label: 'In person' },
  { value: 'letter', label: 'Letter' },
  { value: 'other', label: 'Other' },
];

function meta(map: Record<string, { label: string; tone: PillTone }>, status: string) {
  return map[status] ?? { label: status, tone: 'neutral' as PillTone };
}

/**
 * WHAT this case is attached to — the kind of binding, in words.
 *
 * It used to be `Group <uuid>` / `Unit <id>` / `Occupancy <id>`: the id is the
 * only thing the case carries for its binding, and printing it made the Subject
 * column unreadable without telling anyone anything (docs/no-ids-in-ui.md). The
 * WHO is already the Requester column beside it; what is left to say here is
 * which kind of record the accommodation hangs off.
 */
function subjectLabel(c: RraCaseView): string {
  // RRA v2: opened for someone with no application and no unit yet.
  if (c.unbound) return 'Not yet applied';
  if (c.bindings.applicationGroupId) return 'Rental application';
  if (c.bindings.unitId) return 'Unit';
  if (c.bindings.occupancyId) return 'Occupancy';
  return '—';
}

function ageDays(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  const days = Math.max(0, Math.floor(ms / 86_400_000));
  return days === 0 ? 'today' : `${days}d`;
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function yesNo(b: boolean | undefined): string {
  return b === undefined ? '—' : b ? 'Yes' : 'No';
}

/** WEL-119: every animal fact the applicant entered, for the request-details view. */
function animalDetailPairs(a: NonNullable<RraItemView['animal']>): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  if (a.name) out.push(['Name', a.name]);
  const type = a.type === 'other' ? a.otherDescription || 'Other' : a.type;
  if (type) out.push(['Type', String(type)]);
  if (a.breed) out.push(['Breed', a.breed]);
  if (a.weightLbs !== undefined) out.push(['Weight', `${a.weightLbs} lb`]);
  if (a.age !== undefined) out.push(['Age', String(a.age)]);
  if (a.gender) out.push(['Gender', a.gender]);
  if (a.spayedNeutered !== undefined) out.push(['Spayed/neutered', a.spayedNeutered ? 'Yes' : 'No']);
  if (a.licenseNumber) out.push(['License #', a.licenseNumber]);
  return out;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AccommodationsPage() {
  const [data, setData] = useState<RraCasesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restricted, setRestricted] = useState(false);
  const [openCaseId, setOpenCaseId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // RRA v2.1 (A2): the staff email deep-links to /accommodations?case=<caseId>.
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listRraCases());
      setRestricted(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setRestricted(true);
      else setError(rraErrorMessage(err, 'Failed to load accommodation cases'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Open the case named in the URL (on arrival, and if the link changes).
  const caseParam = searchParams.get('case');
  useEffect(() => {
    if (caseParam) setOpenCaseId(caseParam);
  }, [caseParam]);

  /** Close the detail modal and drop the deep-link param so a refresh doesn't reopen it. */
  const closeCase = useCallback(() => {
    setOpenCaseId(null);
    if (searchParams.has('case')) {
      const next = new URLSearchParams(searchParams);
      next.delete('case');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const cases = useMemo(() => data?.cases ?? [], [data]);
  const [queueView, setQueueView] = useState<QueueView>('all');
  const shownCases = useMemo(() => visibleCases(cases, queueView), [cases, queueView]);
  const actionTotal = useMemo(() => actionCount(cases), [cases]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-7)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Accommodations
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
            Reasonable-accommodation requests — restricted console. Charges and enforcement freeze on
            notice; nothing here appears on the lease.
          </p>
        </div>
        {!restricted && (
          <button type="button" className="btn" data-variant="primary" onClick={() => setCreateOpen(true)}>
            New request
          </button>
        )}
      </div>

      {restricted && (
        <div className="alert" data-tone="warning" role="alert">
          <div className="alert-body">
            <div className="alert-title">Restricted console</div>
            This surface is limited to the configured RRA staff (RRA_STAFF). Ask an administrator for
            access if you work accommodation requests.
          </div>
        </div>
      )}

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      )}

      {!loading && !restricted && !error && data && (
        <>
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>
            {data.counts.open} open · {data.counts.frozen} frozen
            {data.counts.needsAttention > 0 ? ` · ${data.counts.needsAttention} need attention` : ''}
            {' · '}{data.counts.total} total
            {data.counts.test > 0 ? ` · ${data.counts.test} test` : ''}
          </div>

          {cases.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
              No accommodation cases yet. Open one with “New request” — use the test-request option to
              try the whole flow against your own inbox first.
            </div>
          ) : (
            <>
              {/* A closed case can still owe staff an AppFolio task; "Needs action" lists those with the
                  cases flagged for attention, so none is buried under finished history. */}
              <div className="cluster" role="group" aria-label="Which cases to show" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                {(['all', 'action'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="btn"
                    data-variant={queueView === v ? 'primary' : 'secondary'}
                    data-size="sm"
                    aria-pressed={queueView === v}
                    onClick={() => setQueueView(v)}
                  >
                    {QUEUE_VIEW_LABEL[v]}
                    {v === 'action' ? ` (${actionTotal})` : ''}
                  </button>
                ))}
              </div>
              {shownCases.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>{NOTHING_NEEDS_ACTION}</div>
              ) : (
                <CaseQueueTable cases={shownCases} onOpen={setOpenCaseId} />
              )}
            </>
          )}
        </>
      )}

      <CreateCaseModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(caseId) => {
          setCreateOpen(false);
          void load();
          setOpenCaseId(caseId);
        }}
      />
      {openCaseId && (
        <CaseDetailModal caseId={openCaseId} onClose={closeCase} onChanged={() => void load()} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The queue table — every case, closed ones included. A case that holds an AppFolio task staff have
// not marked applied says so on its row, whatever its status.
// ---------------------------------------------------------------------------

export function CaseQueueTable({ cases, onOpen }: { cases: RraCaseView[]; onOpen: (caseId: string) => void }) {
  return (
      <div className="table-wrap">
        <table className="table" data-zebra="true">
          <thead>
            <tr>
              <th scope="col">Requester</th>
              <th scope="col">Subject</th>
              <th scope="col">Scope</th>
              <th scope="col">Status</th>
              <th scope="col">Freeze</th>
              <th scope="col">Age</th>
              <th scope="col" className="col-action" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => {
              const st = meta(CASE_STATUS_META, c.status);
              return (
                <tr key={c.caseId}>
                  <td>
                    <div style={{ fontWeight: 'var(--weight-medium)' }}>
                      {c.requester.name || c.requester.email || '—'}
                      {c.testMode && (
                        <span className="badge" data-size="sm" data-tone="warning" style={{ marginLeft: 'var(--space-2)' }}>
                          TEST
                        </span>
                      )}
                      {c.origin === 'lease_build' && (
                        <span className="badge" data-size="sm" data-tone="info" style={{ marginLeft: 'var(--space-2)' }}>
                          Approved at lease build
                        </span>
                      )}
                    </div>
                    {c.requester.name && c.requester.email && (
                      <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>{c.requester.email}</div>
                    )}
                  </td>
                  <td>{subjectLabel(c)}</td>
                  <td>
                    <span className="cluster" style={{ gap: 'var(--space-1)' }}>
                      {[...new Set(c.items.map((i) => i.scope))].map((s) => (
                        <span key={s} className="badge" data-size="sm">
                          {SCOPE_LABEL[s]}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td>
                    <span className="cluster" style={{ gap: 'var(--space-1)' }}>
                      <span className="pill" data-tone={st.tone}>{st.label}</span>
                      {c.unbound && (
                        <span className="pill" data-tone="neutral">Not yet applied</span>
                      )}
                      {c.needsAttention && (
                        <span className="pill" data-tone="danger" title="Something on this case needs you. Open it to see what.">
                          Needs attention
                        </span>
                      )}
                      {hasPendingAppfolioTask(c) && (
                        <span className="pill" data-tone="warning" title="An AppFolio task on this case has not been marked applied. Open it to see which.">
                          {APPFOLIO_TASK_PENDING}
                        </span>
                      )}
                    </span>
                    {c.waitingOn && (
                      <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
                        {c.waitingOn}
                      </div>
                    )}
                  </td>
                  <td>
                    {c.freezeActive ? (
                      <span className="pill" data-tone="warning">Charges frozen</span>
                    ) : (
                      <span style={{ color: 'var(--fg-subtle)' }}>—</span>
                    )}
                  </td>
                  <td>{ageDays(c.noticeAt)}</td>
                  <td className="col-action">
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      onClick={() => onOpen(c.caseId)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
  );
}

// ---------------------------------------------------------------------------
// Person search — shared by the create modal and the "Attach to an application…"
// action (RRA v2, W2). Names, roles, and addresses only: a result row never
// shows an id (docs/no-ids-in-ui.md); the binding fields ride back on pick.
// ---------------------------------------------------------------------------

const PERSON_SEARCH_MIN_CHARS = 2;
const PERSON_SEARCH_DEBOUNCE_MS = 250;

function personKindLabel(kind: RraPersonHit['kind']): string {
  return kind === 'applicant' ? 'Applicant' : 'Current resident';
}

type PersonSearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; people: RraPersonHit[] }
  | { status: 'error'; message: string };

function PersonSearch({
  inputId,
  applicantsOnly = false,
  emptyMessage,
  autoFocus = false,
  onPick,
}: {
  inputId: string;
  /** Show only people with an open application (the attach action). */
  applicantsOnly?: boolean;
  emptyMessage: string;
  autoFocus?: boolean;
  onPick: (person: RraPersonHit) => void;
}) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<PersonSearchState>({ status: 'idle' });

  // Debounced search; a newer keystroke cancels the older request's result.
  useEffect(() => {
    const q = query.trim();
    if (q.length < PERSON_SEARCH_MIN_CHARS) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    const timer = window.setTimeout(() => {
      searchRraPeople(q)
        .then((res) => {
          if (!cancelled) setState({ status: 'done', people: res.people });
        })
        .catch((err: unknown) => {
          if (!cancelled) setState({ status: 'error', message: rraErrorMessage(err, 'The search failed. Try again.') });
        });
    }, PERSON_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const people =
    state.status === 'done' ? (applicantsOnly ? state.people.filter((p) => p.kind === 'applicant') : state.people) : [];

  return (
    <div className="stack-sm">
      <input
        id={inputId}
        className="input"
        type="search"
        autoComplete="off"
        autoFocus={autoFocus}
        aria-label="Search by name"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name…"
      />
      {state.status === 'idle' && query.trim().length > 0 && (
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>Type at least two letters to search.</div>
      )}
      {state.status === 'loading' && (
        <div role="status" style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>Searching…</div>
      )}
      {state.status === 'error' && (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{state.message}</div>
      )}
      {state.status === 'done' && people.length === 0 && (
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{emptyMessage}</div>
      )}
      {people.length > 0 && (
        <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {people.map((p, i) => (
            <li key={`${p.kind}-${p.name}-${p.where}-${i}`}>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                onClick={() => onPick(p)}
                style={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  height: 'auto',
                  whiteSpace: 'normal',
                  padding: 'var(--space-2) var(--space-3)',
                }}
              >
                <span style={{ display: 'block', minWidth: 0 }}>
                  <span className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'var(--weight-bold)' }}>{p.name}</span>
                    <span className="badge" data-size="sm" data-tone={p.kind === 'applicant' ? 'info' : undefined}>
                      {personKindLabel(p.kind)}
                    </span>
                  </span>
                  <span style={{ display: 'block', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{p.where}</span>
                  {p.email && (
                    <span style={{ display: 'block', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{p.email}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create modal — with the prominent "Test request" affordance. RRA v2: the
// subject is picked BY PERSON (applicants and current residents), or staff
// choose "Not an applicant yet" and give a name + email; the request then
// attaches itself to the application when the person applies.
// ---------------------------------------------------------------------------

function CreateCaseModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (caseId: string) => void;
}) {
  const containerRef = useFocusTrap(open);

  const [testMode, setTestMode] = useState(true);
  const [who, setWho] = useState<'known' | 'new'>('known');
  const [picked, setPicked] = useState<RraPersonHit | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState<RraChannel>('phone');
  // RRA v2.2 (WEL-180): a case carries 1-10 requests, each its own kind and statement. No animal
  // details are entered here — approval picks the animals (pre-lease) or the household lists them (tenancy).
  const [rows, setRows] = useState<RequestRow[]>(() => [blankRequestRow()]);
  const [rowsTouched, setRowsTouched] = useState(false);
  // RRA v2.1 (A1): "What do we already know?" — required for every new case.
  const [gate, setGate] = useState<GateDraft>(emptyGateDraft);
  const [gateTouched, setGateTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Back to a blank form (closing and a successful create both start fresh). */
  const resetForm = () => {
    setTestMode(true);
    setWho('known');
    setPicked(null);
    setName('');
    setEmail('');
    setPhone('');
    setChannel('phone');
    setRows([blankRequestRow()]);
    setRowsTouched(false);
    setGate(emptyGateDraft());
    setGateTouched(false);
    setError(null);
  };
  const close = () => {
    resetForm();
    onClose();
  };
  useEscapeKey(close, open);
  useInertBackground(open);

  if (!open) return null;

  // The gate is one set of answers for the whole case; the service-animal box is offered when ANY request is one.
  const gateScope = gateScopeFor(rows.map((r) => r.scope));
  const pickingPerson = !testMode && who === 'known';
  const gateErrors = validateGateDraft(gate);

  const pick = (person: RraPersonHit) => {
    setPicked(person);
    setName(person.name);
    setEmail(person.email ?? '');
    setPhone(person.phone ?? '');
    setError(null);
  };

  const submit = async () => {
    setError(null);
    if (!testMode) {
      if (who === 'known' && !picked) {
        setError('Pick someone from the search, or choose “Not an applicant yet”.');
        return;
      }
      if (who === 'new' && !email.trim()) {
        setError('Enter their email — we use it to attach the request when they apply.');
        return;
      }
    }
    if (!rowsValid(rows)) {
      setRowsTouched(true);
      return;
    }
    const gateBody = gateDraftToBody(gate, gateScope);
    if (!gateBody) {
      setGateTouched(true);
      return;
    }
    setBusy(true);
    try {
      const body: CreateRraCaseBody = {
        // Test requests pass no subject (the server creates a throwaway one).
        bindings:
          pickingPerson && picked
            ? picked.kind === 'applicant'
              ? {
                  ...(picked.applicationGroupId ? { applicationGroupId: picked.applicationGroupId } : {}),
                  ...(picked.personKey ? { personKey: picked.personKey } : {}),
                }
              : {
                  ...(picked.unitId ? { unitId: picked.unitId } : {}),
                  ...(picked.occupancyId ? { occupancyId: picked.occupancyId } : {}),
                }
            : {},
        requester: {
          ...(name.trim() ? { name: name.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
        channel,
        testMode,
        ...(!testMode && who === 'new' ? { notAnApplicantYet: true } : {}),
        items: itemsFromRows(rows),
        gate: gateBody,
      };
      const res = await createRraCase(body);
      resetForm();
      onCreated(res.case.caseId);
    } catch (err) {
      setError(rraErrorMessage(err, 'Failed to create the case'));
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={close} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="rra-create-title" className="modal">
        <div className="modal-header">
          <h2 id="rra-create-title" className="modal-title">New accommodation request</h2>
          <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={close}>
            Close
          </button>
        </div>
        <div className="modal-body">
          <div className="stack">
            {/* THE TEST AFFORDANCE — prominent, on by default until staff
                deliberately open a real case. */}
            <label
              className="card"
              style={{
                display: 'flex',
                gap: 'var(--space-3)',
                alignItems: 'flex-start',
                cursor: 'pointer',
                borderColor: testMode ? 'var(--warning, #b58900)' : undefined,
              }}
            >
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => {
                  setTestMode(e.target.checked);
                  setPicked(null);
                  setError(null);
                }}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontWeight: 'var(--weight-medium)' }}>Test request</span>
                <span style={{ display: 'block', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
                  Type any email and drive the whole flow to a test inbox. A test case can only email
                  allowlisted staff addresses, is badged TEST, stays out of the real counts, and can be
                  deleted afterwards.
                </span>
              </span>
            </label>

            {/* WHO IS ASKING — real requests only. A test request keeps the typed-email behavior. */}
            {!testMode && (
              <fieldset className="fieldset stack-sm">
                <legend>Who is asking?</legend>
                <div className="cluster" style={{ gap: 'var(--space-5)' }}>
                  <label className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="rra-who"
                      checked={who === 'known'}
                      onChange={() => {
                        setWho('known');
                        setPicked(null);
                        setError(null);
                      }}
                    />
                    <span>Someone who has applied or lives with us</span>
                  </label>
                  <label className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="rra-who"
                      checked={who === 'new'}
                      onChange={() => {
                        setWho('new');
                        setPicked(null);
                        setError(null);
                      }}
                    />
                    <span>Not an applicant yet</span>
                  </label>
                </div>

                {who === 'known' && !picked && (
                  <PersonSearch
                    inputId="rra-person-search"
                    emptyMessage="No one by that name has an open application or a current tenancy. Choose “Not an applicant yet” instead."
                    onPick={pick}
                  />
                )}
                {who === 'known' && picked && (
                  <div
                    className="cluster"
                    style={{ gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--text-sm)' }}
                  >
                    <span>
                      <span style={{ color: 'var(--fg-muted)' }}>Attached to: </span>
                      {picked.where} · {personKindLabel(picked.kind)}
                    </span>
                    <button
                      type="button"
                      className="btn"
                      data-variant="ghost"
                      data-size="sm"
                      onClick={() => setPicked(null)}
                    >
                      Change
                    </button>
                  </div>
                )}
                {who === 'new' && (
                  <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
                    {"We'll attach this request to their application automatically once they apply, using the email below. Ask which email they'll use on their application."}
                  </div>
                )}
              </fieldset>
            )}

            <div className="field">
              <label className="label" htmlFor="rra-req-name">Requester name</label>
              <input id="rra-req-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Rae Quester" />
            </div>
            <div className="field">
              <label className="label" htmlFor="rra-req-email">
                Requester email {testMode ? '(your test inbox)' : who === 'new' ? '(required)' : ''}
              </label>
              <input id="rra-req-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={testMode ? 'you@welcomehps.com' : 'requester@example.com'} />
            </div>
            <div className="field">
              <label className="label" htmlFor="rra-req-phone">Phone</label>
              <input id="rra-req-phone" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="rra-channel">How the request reached us</label>
              <select id="rra-channel" className="input" value={channel} onChange={(e) => setChannel(e.target.value as RraChannel)}>
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* RRA v2.2: one card per request — kind and what is being asked; no animal fields. */}
            <RequestRows rows={rows} onChange={setRows} disabled={busy} showErrors={rowsTouched} />

            {/* A1: what we already know — no case without it. No letters exist before the case does. */}
            <GateFields
              value={gate}
              onChange={(next) => {
                setGate(next);
                setGateTouched(true);
              }}
              letters={[]}
              scope={gateScope}
              errors={gateTouched ? gateErrors : []}
            />

            {error && (
              <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={close}>Cancel</button>
          <button
            type="button"
            className="btn"
            data-variant="primary"
            disabled={busy || gateErrors.length > 0}
            onClick={() => void submit()}
          >
            {busy ? 'Creating…' : testMode ? 'Create test case' : 'Open case'}
          </button>
        </div>
      </div>
    </>
  );
  return createPortal(dialog, document.body);
}

// ---------------------------------------------------------------------------
// Unbound card — a "not an applicant yet" request in the detail modal. It
// attaches itself when the person applies with the email on file; "Attach to an
// application…" does the same by hand (applicants only), and writes the freeze.
// ---------------------------------------------------------------------------

function UnboundPanel({ record, busy, run }: { record: RraCaseView; busy: string | null; run: RunFn }) {
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<RraPersonHit | null>(null);
  const who = record.requester.name?.trim() || 'the requester';

  return (
    <div className="card">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-2)' }}>Not yet applied</div>
      <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
        {`This request isn't attached to an application yet. It attaches automatically when ${who} applies with the email on file.`}
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        {!searching ? (
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy !== null}
            onClick={() => setSearching(true)}
          >
            Attach to an application…
          </button>
        ) : selected && selected.applicationGroupId ? (
          <div className="stack-sm">
            <div style={{ fontSize: 'var(--text-sm)' }}>
              {`Attach this request to ${selected.name}’s application at ${selected.where}?`}
            </div>
            <div className="cluster" style={{ gap: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn"
                data-variant="primary"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => {
                  const applicationGroupId = selected.applicationGroupId;
                  if (!applicationGroupId) return;
                  void run(
                    'attach',
                    () =>
                      attachRraCase(record.caseId, {
                        applicationGroupId,
                        ...(selected.personKey ? { personKey: selected.personKey } : {}),
                      }),
                    `Attached to the application at ${selected.where}. The freeze is now in place.`,
                  );
                }}
              >
                {busy === 'attach' ? 'Attaching…' : 'Attach'}
              </button>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => setSelected(null)}
              >
                Choose someone else
              </button>
            </div>
          </div>
        ) : (
          <div className="stack-sm">
            <PersonSearch
              inputId="rra-attach-search"
              applicantsOnly
              autoFocus
              emptyMessage="No open application matches that name."
              onPick={setSelected}
            />
            <div>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                onClick={() => setSearching(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detach card — a request that was attached to an application after the fact
// (the watcher found the email, or staff attached it). RRA v2.1 (H2): if it
// landed on the wrong application, one click returns it to "Not yet applied"
// and releases the holds the attach wrote. Mutually exclusive with UnboundPanel.
// ---------------------------------------------------------------------------

function DetachPanel({ record, busy, run }: { record: RraCaseView; busy: string | null; run: RunFn }) {
  return (
    <div className="card">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-3)' }}>
        {`Attached ${record.attachedBy === 'watcher' ? 'automatically' : 'by staff'} on ${fmtDate(record.attachedAt)}`}
      </div>
      <button
        type="button"
        className="btn"
        data-variant="secondary"
        data-size="sm"
        disabled={busy !== null}
        onClick={() =>
          void run(
            'detach',
            () => detachRraCase(record.caseId),
            'Detached. The request is back to “Not yet applied” and its holds were released.',
          )
        }
      >
        {busy === 'detach' ? 'Detaching…' : 'Not this application — detach'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — the working panels.
// ---------------------------------------------------------------------------

function CaseDetailModal({
  caseId,
  onClose,
  onChanged,
}: {
  caseId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const containerRef = useFocusTrap(true);
  useEscapeKey(onClose, true);
  useInertBackground(true);

  const [record, setRecord] = useState<RraCaseView | null>(null);
  const [verifications, setVerifications] = useState<RraVerificationView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(
    async (opts?: { quiet?: boolean }) => {
      try {
        const res = await getRraCase(caseId);
        setRecord(res.case);
        setVerifications(res.verifications);
      } catch (err) {
        // Quiet: a refresh after a failed action must not replace that action's own message.
        if (!opts?.quiet) setError(rraErrorMessage(err, 'Failed to load the case'));
      }
    },
    [caseId],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = useCallback<RunFn>(
    async (key, fn, successNotice) => {
      setBusy(key);
      setError(null);
      setNotice(null);
      try {
        const result = await fn();
        // A notice may depend on what the action returned (e.g. how many requests a Revise cancelled).
        const text = typeof successNotice === 'function' ? successNotice(result) : successNotice;
        if (text) setNotice(text);
        await reload();
        onChanged();
      } catch (err) {
        setError(rraErrorMessage(err, 'The action failed'));
        // An action can fail AFTER part of it happened (an approval that stands but whose next step needs
        // staff). Show the case as it now stands, so the card behind the red banner tells the truth.
        await reload({ quiet: true });
        onChanged();
      } finally {
        setBusy(null);
      }
    },
    [reload, onChanged],
  );

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="rra-detail-title" className="modal" data-size="lg">
        <div className="modal-header">
          <div className="stack-sm" style={{ minWidth: 0 }}>
            <h2 id="rra-detail-title" className="modal-title">
              {record?.requester.name || record?.requester.email || 'Accommodation case'}
              {record?.testMode && (
                <span className="badge" data-tone="warning" style={{ marginLeft: 'var(--space-2)' }}>TEST</span>
              )}
              {record?.origin === 'lease_build' && (
                <span className="badge" data-tone="info" style={{ marginLeft: 'var(--space-2)' }}>
                  Approved at lease build
                </span>
              )}
            </h2>
            {record && (
              <div className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
                <span className="pill" data-tone={meta(CASE_STATUS_META, record.status).tone}>
                  {meta(CASE_STATUS_META, record.status).label}
                </span>
                {record.freezeActive && <span className="pill" data-tone="warning">Charges frozen</span>}
                {record.needsAttention && <span className="pill" data-tone="danger">Needs attention</span>}
                <span>{subjectLabel(record)}</span>
              </div>
            )}
            {record?.waitingOn && (
              <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{record.waitingOn}</div>
            )}
            {record?.requestOpenLine && (
              <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{record.requestOpenLine}</div>
            )}
          </div>
          <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">
          {!record ? (
            error ? (
              <div className="alert" data-tone="danger" role="alert"><div className="alert-body">{error}</div></div>
            ) : (
              <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>
            )
          ) : (
            <div className="stack">
              {notice && (
                <div className="alert" data-tone="success" role="status"><div className="alert-body">{notice}</div></div>
              )}
              {error && (
                <div className="alert" data-tone="danger" role="alert"><div className="alert-body">{error}</div></div>
              )}

              {/* WEL-104: a lease-build case never ran intake/gate/verification —
                  those panels don't apply (the accommodation was already known
                  and approved when staff designated it in the builder). Explain
                  instead of showing dead send buttons. */}
              {record.origin === 'lease_build' && (
                <div className="alert" data-tone="info">
                  <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>
                    <div className="alert-title">Approved at lease build</div>
                    Staff designated this animal as an already-approved reasonable
                    accommodation directly in the lease builder — its animal rent and
                    deposit are waived on the lease (no lease language; the $0 is the
                    only visible effect). Intake, apparent-gate, and verification do
                    not apply to a build-time approval. Undoing it happens in the
                    lease builder, which withdraws this case.
                  </div>
                </div>
              )}
              <AttentionPanel record={record} busy={busy} run={run} />
              {record.unbound && <UnboundPanel record={record} busy={busy} run={run} />}
              {record.detachable && <DetachPanel record={record} busy={busy} run={run} />}
              <FreezePanel record={record} busy={busy} run={run} />
              {record.origin !== 'lease_build' && <IntakePanel record={record} busy={busy} run={run} />}
              <AddRequestPanel record={record} busy={busy} run={run} add={(body) => addRraRequest(record.caseId, body)} />
              {record.items.map((item) => (
                <ItemPanel key={item.itemId} record={record} item={item} busy={busy} run={run} />
              ))}
              {record.origin !== 'lease_build' && (
                <VerificationPanel record={record} verifications={verifications} busy={busy} run={run} />
              )}
              <CaseActionsPanel record={record} busy={busy} run={run} onDeleted={() => { onChanged(); onClose(); }} />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
  return createPortal(dialog, document.body);
}

/** The console's action runner. `successNotice` is a fixed sentence, or computed from what the action returned. */
type RunFn = RraRunFn;

// --- Freeze status -----------------------------------------------------------

export function FreezePanel({ record, busy, run }: { record: RraCaseView; busy: string | null; run: RunFn }) {
  const actions = record.freeze?.appfolioActions ?? [];
  return (
    <div className="card">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-3)' }}>Freeze status</div>
      {record.holds.length === 0 ? (
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>No holds recorded for this case.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <tbody>
              {record.holds.map((h) => (
                <tr key={h.holdId}>
                  <td style={{ color: 'var(--fg-muted)' }}>
                    {h.actionType === 'lease_generation' ? 'Lease generation' : h.actionType === 'animal_charge' ? 'Animal charges' : h.actionType}
                  </td>
                  <td>
                    {h.active ? (
                      <span className="pill" data-tone="warning">Held</span>
                    ) : (
                      <span className="pill" data-tone="neutral">
                        Released {fmtDate(h.releasedAt)}{h.releasedByDisplay ? ` by ${h.releasedByDisplay}` : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-2)' }}>
        Holds are a record, not a block. A lease can be generated and sent while a request is open; mark the
        animal(s) it concerns in the builder&rsquo;s Animals section and the lease carries a pending-decision
        clause for them. Holds lift automatically on the final decision.
      </div>

      {actions.length > 0 && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
            AppFolio tasks
          </div>
          <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
            {FREEZE_TASKS_INTRO}
          </div>
          <div className="stack-sm">
            {actions.map((a) => (
              <div key={a.actionId} className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)' }}>
                  {freezeActionLabel(a.kind)} <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>raised {fmtDate(a.createdAt)}{a.notifiedAt ? ' · team emailed' : record.testMode ? ' · test case — no email' : ' · email failed'}</span>
                  {freezeActionSummary(a) && (
                    <span style={{ display: 'block', color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
                      {freezeActionSummary(a)}
                    </span>
                  )}
                </span>
                {a.status === 'applied' ? (
                  <span className="pill" data-tone="success">
                    Applied {fmtDate(a.appliedAt)}{a.appliedByDisplay ? ` by ${a.appliedByDisplay}` : ''}
                  </span>
                ) : a.status === 'not_needed' ? (
                  <span className="pill" data-tone="neutral">Not needed</span>
                ) : (
                  <>
                    <span className="pill" data-tone="warning">Pending in AppFolio</span>
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(
                          `freeze-applied:${a.actionId}`,
                          () => markRraFreezeActionApplied(record.caseId, a.actionId),
                          'Marked applied in AppFolio.',
                        )
                      }
                    >
                      {busy === `freeze-applied:${a.actionId}` ? 'Marking…' : 'Mark applied in AppFolio'}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Intake ------------------------------------------------------------------

/** RRA v2.1 (W1): how a signing of permission reached us, in words (DRAFT). */
const PERMISSION_VIA_WORDS = {
  request_form: 'on their form',
  permission_link: 'on the permission page',
  uploaded_document: 'on a document they gave us',
} as const;

/** RRA v2.1 (W1): the staff fill-in waits for a household member's name and age answer (DRAFT). */
const STAFF_WHO_INCOMPLETE = 'Add their first and last name and whether they are under 18, or choose “Me”.';

/** The staff fill-in's subject state, seeded from what is already on the case. */
function subjectValueFrom(subject: RraSubjectView | null): SubjectValue {
  if (!subject) return { choice: '', name: '', minor: undefined };
  if (subject.kind === 'self') return { choice: 'me', name: '', minor: undefined };
  return { choice: 'someone', name: subject.name, minor: subject.minor };
}

/** The Intake panel's staff fill-in answers for one service-animal item (the two permitted questions). */
interface ServiceAnswers {
  required: boolean | undefined;
  support: string;
}

export function IntakePanel({
  record,
  busy,
  run,
  initialShowStaffFill = false,
}: {
  record: RraCaseView;
  busy: string | null;
  run: RunFn;
  /** Test seam: render the staff fill-in already open. */
  initialShowStaffFill?: boolean;
}) {
  const [showStaffFill, setShowStaffFill] = useState(initialShowStaffFill);
  const [showSendNotice, setShowSendNotice] = useState(false);
  const [subjectEmail, setSubjectEmail] = useState('');
  const submitted = Boolean(record.intake.submittedAt);
  const openItems = record.items.filter(isOpenItem);
  // RRA v2.2: the staff fill-in covers only the requests still waiting for the requester — one that
  // already carries a `request` was answered (on the form, or by an earlier fill-in), and one that
  // existed when an older intake was submitted counts as answered too (lib/rraRequests isUnanswered).
  const fillItems = unansweredItems(record.items, record.intake.submittedAt);
  const perm = record.permission;
  const shape = perm.formShape;

  // RRA v2.1 (I5): the staff fill-in mirrors the shape the requester form would show — who it is for,
  // what they are asking us to change, the optional help box (need in the gap, never a service
  // animal), the two service-animal questions, and the two "stated" boxes. All optional: staff record
  // only what the requester actually said.
  const [who, setWho] = useState<SubjectValue>(() => subjectValueFrom(perm.subject));
  const [reqDrafts, setReqDrafts] = useState<Record<string, RequestDraft>>({});
  const [svcAnswers, setSvcAnswers] = useState<Record<string, ServiceAnswers>>({});
  const [statedDisability, setStatedDisability] = useState(false);
  const [statedNeed, setStatedNeed] = useState(false);
  const reqDraftFor = (item: RraItemView): RequestDraft =>
    reqDrafts[item.itemId] ?? requestDraftFromItem(item, { preselect: false });
  const updateReqDraft = (item: RraItemView, patch: Partial<RequestDraft>): void =>
    setReqDrafts((prev) => ({
      ...prev,
      [item.itemId]: { ...(prev[item.itemId] ?? requestDraftFromItem(item, { preselect: false })), ...patch },
    }));
  const svcFor = (itemId: string): ServiceAnswers => svcAnswers[itemId] ?? { required: undefined, support: '' };

  const voice: RraCopySubject = formVoice({ subjectChoice: who.choice, subjectName: who.name, subjectMinor: who.minor });
  const needAffirmationShown = fillItems.some((i) => shape.needItemIds.includes(i.itemId) && i.scope !== 'service_animal');
  const whoIncomplete = who.choice === 'someone' && (!who.name.trim() || who.minor === undefined);

  // A1.4: sending the form before the gate is recorded would send the FULL form.
  const ungated = openItems.filter((i) => !i.evaluation);
  const sendIntake = (): Promise<void> => run('send-intake', () => sendRraIntake(record.caseId), 'Intake link sent.');
  const askKey = 'permission-ask';
  const openAsk = perm.ask && !perm.ask.answeredAt ? perm.ask : null;

  return (
    <div className="card">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-3)' }}>Intake</div>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            <tr><td style={{ color: 'var(--fg-muted)', width: '40%' }}>Form sent</td><td>{fmtDate(record.intake.sentAt)}{(record.intake.manualSends?.length ?? 0) > 0 ? ` (+${record.intake.manualSends!.length} manual)` : ''}</td></tr>
            <tr><td style={{ color: 'var(--fg-muted)' }}>First opened</td><td>{fmtDate(record.intake.openedAt)}</td></tr>
            <tr>
              <td style={{ color: 'var(--fg-muted)' }}>Submitted</td>
              <td>
                {submitted
                  ? `${fmtDate(record.intake.submittedAt)} — ${
                      record.intake.mode === 'staff_filled'
                        ? 'staff fill-in (stated)'
                        : perm.intakeConfirmOnly
                          ? 'requester form (confirmed — nothing signed)'
                          : 'requester form (signed)'
                    }`
                  : '—'}
              </td>
            </tr>
            {submitted && fillItems.length > 0 && (
              <tr>
                <td style={{ color: 'var(--fg-muted)' }}>Waiting on the requester</td>
                <td>
                  {fillItems.length === 1
                    ? 'One request was added after they submitted — the form is open for them to answer it.'
                    : `${fillItems.length} requests were added after they submitted — the form is open for them to answer them.`}
                </td>
              </tr>
            )}
            {record.intake.bouncedAt && (
              <tr>
                <td style={{ color: 'var(--fg-muted)' }}>Email bounced</td>
                <td>
                  <span className="pill" data-tone="danger">Bounced {fmtDate(record.intake.bouncedAt)}</span>{' '}
                  <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
                    Reminders stopped; the case stays open. Reach the requester another way — a manual
                    re-send re-arms the reminders.
                  </span>
                </td>
              </tr>
            )}
            {(record.intake.reminderCount ?? 0) > 0 && (
              <tr>
                <td style={{ color: 'var(--fg-muted)' }}>Reminders</td>
                <td>{record.intake.reminderCount} sent · last {fmtDate(record.intake.lastReminderAt)}</td>
              </tr>
            )}
            {/* RRA v2.1 (D1, A6): every signing of permission, the open ask, and the button. */}
            <tr>
              <td style={{ color: 'var(--fg-muted)', verticalAlign: 'top' }}>Permission to contact</td>
              <td>
                {perm.grants.length === 0 && !perm.ask ? (
                  <span style={{ color: 'var(--fg-muted)' }}>Nobody yet — only the requester, or the adult it is for, can give permission.</span>
                ) : null}
                {perm.grants.map((g, gi) => (
                  <div key={`g-${gi}`} style={{ marginBottom: 'var(--space-2)' }}>
                    Signed by {g.signerWords} · {fmtDate(g.signedAt)} · {PERMISSION_VIA_WORDS[g.via]}
                    {g.withdrawn ? <> <span className="pill" data-tone="danger">Withdrawn</span></> : null}
                    {g.expired ? <> <span className="pill" data-tone="neutral">Expired</span></> : null}
                    {g.verifiers.length > 0 ? (
                      <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                        {g.verifiers.map((v, i) => (
                          <li key={`v-${gi}-${i}`}>
                            {[v.name, v.role].filter(Boolean).join(' — ') || 'someone'}
                            {(v.email || v.phone) && (
                              <span style={{ color: 'var(--fg-muted)' }}>
                                {' '}({[v.email, v.phone].filter(Boolean).join(' · ')})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span style={{ color: 'var(--fg-muted)' }}> · no one named</span>
                    )}
                  </div>
                ))}
                {perm.ask ? (
                  <div style={{ color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
                    Asked {perm.ask.signerName} who can confirm · {fmtDate(perm.ask.sentAt)}
                    {perm.ask.answeredAt
                      ? ` · answered ${fmtDate(perm.ask.answeredAt)}${perm.ask.answer === 'letter' ? ' with a letter' : perm.ask.answer === 'named' ? ' by naming someone' : ''}`
                      : ' · waiting'}
                  </div>
                ) : null}
                {perm.canAsk ? (
                  <div className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                    {perm.needsSubjectEmail && !openAsk ? (
                      <div className="field" style={{ minWidth: 220 }}>
                        <label htmlFor="rra-staff-subject-email">{`${perm.subjectName}'s email`}</label>
                        <input
                          id="rra-staff-subject-email"
                          className="input"
                          type="email"
                          value={subjectEmail}
                          disabled={busy !== null}
                          onChange={(e) => setSubjectEmail(e.target.value)}
                        />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      disabled={busy !== null || (perm.needsSubjectEmail && !openAsk && !subjectEmail.trim())}
                      onClick={() =>
                        void run(
                          askKey,
                          () => askRraPermission(record.caseId, subjectEmail.trim() ? { subjectEmail: subjectEmail.trim() } : {}),
                          openAsk ? 'The link was sent again.' : 'The link was sent.',
                        )
                      }
                    >
                      {busy === askKey ? 'Sending…' : openAsk ? `Resend to ${perm.ask!.signerName}` : perm.askLabel}
                    </button>
                  </div>
                ) : null}
              </td>
            </tr>
            {record.uploadedLetters.length > 0 && (
              <tr>
                <td style={{ color: 'var(--fg-muted)', verticalAlign: 'top' }}>Uploaded letters</td>
                <td>
                  <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
                    {record.uploadedLetters.map((l, i) => (
                      <li key={`l-${i}`}>
                        {l.filename}{' '}
                        <button
                          type="button"
                          className="btn"
                          data-variant="ghost"
                          data-size="xs"
                          disabled={busy !== null}
                          onClick={() =>
                            void run(`letter:${i}`, async () => {
                              const { url } = await rraLetterDownloadUrl(record.caseId, i);
                              const a = document.createElement('a');
                              a.href = url;
                              a.target = '_blank';
                              a.rel = 'noopener';
                              a.click();
                            })
                          }
                        >
                          {busy === `letter:${i}` ? 'Opening…' : 'Download'}
                        </button>
                        <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
                          {' '}· uploaded {fmtDate(l.uploadedAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          disabled={busy !== null || !record.requester.email}
          title={record.requester.email ? '' : 'No requester email on file'}
          onClick={() => {
            if (ungated.length > 0) setShowSendNotice(true);
            else void sendIntake();
          }}
        >
          {busy === 'send-intake' ? 'Sending…' : record.intake.sentAt ? 'Resend the request form' : 'Send the request form'}
        </button>
        {submitted && (
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            disabled={busy !== null}
            onClick={() =>
              void run('request-pdf', async () => {
                const blob = await rraCaseRequestPdf(record.caseId);
                window.open(URL.createObjectURL(blob), '_blank', 'noopener');
              })
            }
          >
            {busy === 'request-pdf' ? 'Opening…' : 'Download the request'}
          </button>
        )}
        {fillItems.length > 0 && (
          <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={() => setShowStaffFill((v) => !v)}>
            Staff fill-in…
          </button>
        )}
      </div>
      {showSendNotice && ungated.length > 0 && (
        <div className="alert" data-tone="info" role="status" style={{ marginTop: 'var(--space-3)' }}>
          <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>
            <div>{GATE.sendWithoutGate}</div>
            <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => {
                  setShowSendNotice(false);
                  document.getElementById(`rra-item-${ungated[0]!.itemId}`)?.scrollIntoView?.({ block: 'center' });
                }}
              >
                Record what we know
              </button>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => {
                  setShowSendNotice(false);
                  void sendIntake();
                }}
              >
                Send the form anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {showStaffFill && fillItems.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginBottom: 'var(--space-3)' }}>
            Record what the requester STATED (phone or in person). This is not a signature, and it cannot
            give permission to contact anyone — only the requester, or the adult it is for, can do that.
          </div>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <SubjectFields
              idPrefix="staff-who"
              value={who}
              onChange={(p) => setWho((prev) => ({ ...prev, ...p }))}
              disabled={busy !== null}
              heading={WHO_FOR.heading}
            />
          </div>
          {fillItems.map((item) => {
            const d = reqDraftFor(item);
            const ordinal = itemOrdinalLabel(record, item);
            const n = ordinal ? ` (${ordinal})` : '';
            const isAnimal = isAnimalItem(item);
            const inNeedGap = shape.needItemIds.includes(item.itemId);
            const isService = item.scope === 'service_animal';
            const svc = svcFor(item.itemId);
            return (
              <div key={`staff-req-${item.itemId}`} className="stack" style={{ marginBottom: 'var(--space-4)' }}>
                <RequestStatementFields
                  idPrefix={`staff-req-${item.itemId}`}
                  heading={`Requester says — what they’re asking us to change${n}`}
                  value={d}
                  subject={voice}
                  isAnimal={isAnimal}
                  disabled={busy !== null}
                  onChange={(patch) => updateReqDraft(item, patch)}
                />
                {inNeedGap && !isService ? (
                  <HelpBox
                    idPrefix={`staff-help-${item.itemId}`}
                    value={d.howChangeHelps}
                    onChange={(v) => updateReqDraft(item, { howChangeHelps: v })}
                    subject={voice}
                    animal={isAnimal}
                    disabled={busy !== null}
                    heading={null}
                    label={`Requester says — how it would help${n}`}
                  />
                ) : null}
                {isService && inNeedGap ? (
                  <>
                    <fieldset style={{ border: 0, padding: 0, marginInline: 0, minWidth: 0 }}>
                      <legend style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', padding: 0 }}>
                        Is this animal needed because of a disability?{n}
                      </legend>
                      <div className="cluster" style={{ gap: 'var(--space-5)' }}>
                        <label className="choice">
                          <input
                            type="radio"
                            name={`staff-svc-${item.itemId}`}
                            checked={svc.required === false}
                            disabled={busy !== null}
                            onChange={() => setSvcAnswers((p) => ({ ...p, [item.itemId]: { ...svcFor(item.itemId), required: false } }))}
                          />{' '}
                          No
                        </label>
                        <label className="choice">
                          <input
                            type="radio"
                            name={`staff-svc-${item.itemId}`}
                            checked={svc.required === true}
                            disabled={busy !== null}
                            onChange={() => setSvcAnswers((p) => ({ ...p, [item.itemId]: { ...svcFor(item.itemId), required: true } }))}
                          />{' '}
                          Yes
                        </label>
                      </div>
                    </fieldset>
                    <div className="field">
                      <label htmlFor={`staff-svc-support-${item.itemId}`}>
                        What support does the animal provide, or what tasks does it perform?{n}
                      </label>
                      <textarea
                        id={`staff-svc-support-${item.itemId}`}
                        className="input"
                        rows={3}
                        maxLength={1000}
                        value={svc.support}
                        disabled={busy !== null}
                        onChange={(e) => setSvcAnswers((p) => ({ ...p, [item.itemId]: { ...svcFor(item.itemId), support: e.target.value } }))}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            );
          })}
          {shape.askDisability ? (
            <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
              <input type="checkbox" checked={statedDisability} onChange={(e) => setStatedDisability(e.target.checked)} />
              {voice.readerIsSubject ? 'Stated they have a disability' : `Stated ${voice.name} has a disability`}
            </label>
          ) : null}
          {needAffirmationShown ? (
            <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
              <input type="checkbox" checked={statedNeed} onChange={(e) => setStatedNeed(e.target.checked)} />
              Stated they need what they&rsquo;re asking for because of it
            </label>
          ) : null}
          <div style={{ marginTop: 'var(--space-3)' }}>
            {/* Review fix: "Someone who lives with me" with no name or no age answer would silently be
                recorded in the requester's own voice — the button waits for both (DRAFT hint). */}
            {whoIncomplete ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
                {STAFF_WHO_INCOMPLETE}
              </div>
            ) : null}
            <button
              type="button"
              className="btn"
              data-variant="primary"
              data-size="sm"
              disabled={busy !== null || whoIncomplete}
              onClick={() =>
                void run(
                  'staff-fill',
                  () =>
                    staffFillRraIntake(record.caseId, {
                      formVersion: 'v2.1',
                      ...(who.choice === 'me'
                        ? { subject: { kind: 'self' as const } }
                        : who.choice === 'someone' && who.name.trim() && who.minor !== undefined
                          ? { subject: { kind: 'household_member' as const, name: who.name.trim(), minor: who.minor } }
                          : {}),
                      items: fillItems.map((i) => {
                        const isService = i.scope === 'service_animal';
                        const inNeedGap = shape.needItemIds.includes(i.itemId);
                        const svc = svcFor(i.itemId);
                        const support = svc.support.trim();
                        return {
                          itemId: i.itemId,
                          ...staffItemFields(reqDraftFor(i), {
                            isAnimal: isAnimalItem(i),
                            helpShown: inNeedGap && !isService,
                          }),
                          ...(isService && inNeedGap && svc.required !== undefined
                            ? { requiredBecauseOfDisability: svc.required }
                            : {}),
                          ...(isService && inNeedGap && support ? { supportProvided: support } : {}),
                        };
                      }),
                      stated: {
                        ...(shape.askDisability && statedDisability ? { hasDisability: true } : {}),
                        ...(needAffirmationShown && statedNeed ? { needsBecauseOfDisability: true } : {}),
                      },
                    }),
                  'Intake recorded as stated.',
                )
              }
            >
              {busy === 'staff-fill' ? 'Recording…' : 'Record intake (stated)'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- One item: gate + decision ------------------------------------------------

export function ItemPanel({
  record,
  item,
  busy,
  run,
  initialPicker = null,
  initialRoster = { status: 'loading' },
}: {
  record: RraCaseView;
  item: RraItemView;
  busy: string | null;
  run: RunFn;
  /** Test seams: render the panel with the animal picker already open, and its roster already read. */
  initialPicker?: ApprovePickerMode | null;
  initialRoster?: RosterLoad;
}) {
  const st = meta(ITEM_STATUS_META, item.status);
  const terminal = ['approved', 'denied', 'withdrawn'].includes(item.status);
  const ev = item.evaluation;

  // Gate form state (A1): one <GateFields> form records the first gate AND every Revise.
  const [gateDraft, setGateDraft] = useState<GateDraft>(emptyGateDraft);
  const [gateTouched, setGateTouched] = useState(false);
  const [showGate, setShowGate] = useState(false);
  // Decision form state.
  const [denyGround, setDenyGround] = useState('');
  const [showDeny, setShowDeny] = useState(false);
  const [moreInfoGap, setMoreInfoGap] = useState('');
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [wdChannel, setWdChannel] = useState<RraWithdrawalChannel>('email');
  const [wdNote, setWdNote] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  // RRA v2.2: the animal picker — Approve on a bound pre-lease animal request, and "Change animals".
  const [picker, setPicker] = useState<ApprovePickerMode | null>(initialPicker);
  const [roster, setRoster] = useState<RosterLoad>(initialRoster);
  const rosterRequest = useRef(0);

  const k = (a: string) => `${a}:${item.itemId}`;

  // H1: this item was approved while the request is still "Not yet applied".
  const awaitingApplication =
    record.approvedAwaitingApplication === true && item.decision?.outcome === 'approved' && item.status === 'approved';
  const voice = requestVoice(record);
  const ordinal = itemOrdinalLabel(record, item);
  const waitingOnRequester = isUnanswered(item, record.intake.submittedAt) && Boolean(record.intake.sentAt);
  // The decision card's words (RRA v2.2): what an approval covers, what it did, and the addendum pointer.
  const covers = coversLine(item);
  const effectLine = approvedEffectLine({ record, item, awaitingApplication });
  const addLine = addendumLine(record, item, fmtDate);

  /** Open the picker and read the household's animals (a miss falls back to the typed row — never an error). */
  const openPicker = (mode: ApprovePickerMode): void => {
    const mine = ++rosterRequest.current;
    setPicker(mode);
    setRoster({ status: 'loading' });
    getRraHouseholdAnimals(record.caseId)
      .then((r) => {
        if (rosterRequest.current !== mine) return;
        setRoster(r.unavailable ? { status: 'unavailable' } : { status: 'ready', animals: r.animals ?? [] });
      })
      .catch(() => {
        if (rosterRequest.current === mine) setRoster({ status: 'unavailable' });
      });
  };
  const closePicker = (): void => {
    rosterRequest.current += 1;
    setPicker(null);
  };

  /** Approve (with the picked animals when the picker ran). A problem AFTER the approval is thrown into the red banner; the approval stands. */
  const approve = (animals?: RraApproveAnimal[]): void => {
    void run(
      k('approve'),
      async () => {
        const res = await approveRraItem(record.caseId, item.itemId, animals ? { animals } : undefined);
        closePicker();
        const outcome = approveOutcome({
          requesterName: record.requester.name,
          res,
          ...(animals ? { picked: animals } : {}),
        });
        if (outcome.tone === 'problem') throw new Error(outcome.text);
        return outcome;
      },
      (result) => (result as { text: string } | undefined)?.text,
    );
  };
  const changeAnimals = (animals: RraApproveAnimal[]): void => {
    void run(
      k('animals'),
      async () => {
        const res = await setRraItemAnimals(record.caseId, item.itemId, { animals });
        closePicker();
        const outcome = changeAnimalsOutcome({ res, picked: animals });
        if (outcome.tone === 'problem') throw new Error(outcome.text);
        return outcome;
      },
      (result) => (result as { text: string } | undefined)?.text,
    );
  };

  const gateLines = ev?.outcome ? gateEvaluationLines(ev) : null;
  const gateErrors = validateGateDraft(gateDraft);
  const openGate = () => {
    setGateDraft(gateDraftFromEvaluation(ev));
    setGateTouched(false);
    setShowGate(true);
  };
  // The Revise / first-record form (the same fields at case open).
  const gateForm = showGate ? (
    <div className="card" style={{ background: 'var(--surface-2, transparent)', marginTop: 'var(--space-3)' }}>
      <GateFields
        value={gateDraft}
        onChange={(next) => {
          setGateDraft(next);
          setGateTouched(true);
        }}
        letters={record.gateLetterNames ?? []}
        scope={item.scope}
        errors={gateTouched ? gateErrors : []}
      />
      <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          className="btn"
          data-variant="primary"
          data-size="sm"
          disabled={busy !== null || gateErrors.length > 0}
          onClick={() => {
            const body = gateDraftToBody(gateDraft, item.scope);
            if (!body) {
              setGateTouched(true);
              return;
            }
            void run(
              k('gate'),
              async () => {
                const res = await recordRraGate(record.caseId, item.itemId, body);
                setShowGate(false);
                return res;
              },
              (res) => {
                const n = (res as Awaited<ReturnType<typeof recordRraGate>> | undefined)?.cancelledAsks ?? 0;
                // A first record with nothing cancelled stays quiet, as it always has.
                return ev?.outcome || n > 0 ? gateRevisedNotice(n) : undefined;
              },
            );
          }}
        >
          {busy === k('gate') ? 'Recording…' : ev?.outcome ? 'Record the revision' : 'Record what we know'}
        </button>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={() => setShowGate(false)}>
          Cancel
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="card" id={`rra-item-${item.itemId}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <div>
          <span className="badge" data-size="sm">{SCOPE_LABEL[item.scope]}</span>{' '}
          {/* RRA v2.2: a case with several requests numbers them; the request itself is the title. */}
          {ordinal && <span style={{ color: 'var(--fg-muted)' }}>{ordinal} · </span>}
          <span style={{ fontWeight: 'var(--weight-medium)' }}>{itemTitle(item, voice)}</span>
          {item.animal?.chargeExemptionCaseItemId && (
            <span className="badge" data-size="sm" data-tone="success" style={{ marginLeft: 'var(--space-2)' }}>
              Charge-exempt
            </span>
          )}
          {waitingOnRequester && (
            <span className="badge" data-size="sm" data-tone="warning" style={{ marginLeft: 'var(--space-2)' }}>
              Waiting on the requester
            </span>
          )}
        </div>
        <span className="pill" data-tone={st.tone}>{st.label}</span>
      </div>

      {/* WEL-119: everything the applicant entered on the request form. RRA v2.1 (W1): the request in the
          record's voice, the one optional help box, the service answers, and only the affirmations that
          were actually asked. */}
      {(item.animal ||
        item.serviceAnimalQuestions ||
        item.attestation ||
        item.request ||
        item.howChangeHelps) && (
        <div
          className="card"
          style={{ background: 'var(--surface-2, transparent)', marginBottom: 'var(--space-3)', padding: 'var(--space-3)' }}
        >
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-medium)', color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
            Request details
          </div>
          {/* What they are asking us to change. These stay on this panel (and the request copy) — never in a feed. */}
          {item.request && requestBullets(item.request, voice, isAnimalItem(item)).length > 0 && (
            <div style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
              <div style={{ color: 'var(--fg-muted)' }}>Request</div>
              <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
                {requestBullets(item.request, voice, isAnimalItem(item)).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          {item.howChangeHelps && (
            <div style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
              <div style={{ color: 'var(--fg-muted)' }}>How it would help</div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--space-1)' }}>{item.howChangeHelps}</div>
            </div>
          )}
          <table style={{ fontSize: 'var(--text-sm)', width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {item.animal &&
                animalDetailPairs(item.animal).map(([label, value]) => (
                  <tr key={`a-${label}`}>
                    <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{label}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              {item.serviceAnimalQuestions?.requiredBecauseOfDisability !== undefined && (
                <tr>
                  <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Required because of a disability</td>
                  <td>{yesNo(item.serviceAnimalQuestions.requiredBecauseOfDisability)}</td>
                </tr>
              )}
              {item.serviceAnimalQuestions?.supportProvided && (
                <tr>
                  <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Support the animal provides</td>
                  <td>{item.serviceAnimalQuestions.supportProvided}</td>
                </tr>
              )}
              {item.animal && item.description && (
                <tr>
                  <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Description</td>
                  <td>{item.description}</td>
                </tr>
              )}
              {item.attestation && (
                <>
                  {/* Only the affirmations the form actually asked (a prong the gate settled is not asked). */}
                  {item.attestation.hasDisability !== undefined && (
                    <tr>
                      <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Affirmed a disability</td>
                      <td>{yesNo(item.attestation.hasDisability)}</td>
                    </tr>
                  )}
                  {item.attestation.needsBecauseOfDisability !== undefined && (
                    <tr>
                      <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Affirmed the need</td>
                      <td>{yesNo(item.attestation.needsBecauseOfDisability)}</td>
                    </tr>
                  )}
                  {item.attestation.attestedAt && (
                    <tr>
                      <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Affirmation</td>
                      <td>
                        {item.attestation.mode === 'signed'
                          ? `Signed by ${item.attestation.signedName ?? '—'}`
                          : 'Recorded as stated by staff'}{' '}
                        on {fmtDate(item.attestation.attestedAt)}
                      </td>
                    </tr>
                  )}
                  {item.attestation.definition === 'rcw_49_60_040' && (
                    <tr>
                      <td style={{ color: 'var(--fg-muted)', paddingRight: 'var(--space-3)', verticalAlign: 'top' }}>Attested under</td>
                      <td>Washington&rsquo;s definition (RCW 49.60.040)</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Apparent-gate — "What do we already know?" (A1). A recorded gate reads in the
          tri-state words and can be revised; a legacy case with none can record one. */}
      {ev?.outcome && gateLines ? (
        <div>
          <div className="alert" data-tone={ev.outcome === 'apparent' ? 'success' : 'info'}>
            <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>
              <div className="alert-title">
                Gate: {ev.outcome === 'apparent' ? 'Apparent / known — verification skipped' : `Gap — ${ev.gapScope?.replace(/_/g, ' ')}`}
              </div>
              <div>{gateLines.disability}</div>
              <div>{gateLines.need}</div>
              <div style={{ color: 'var(--fg-muted)', marginTop: 'var(--space-1)' }}>
                Source: {GATE.sources.find((o) => o.value === ev.source)?.label ?? ev.source ?? '—'}
                {ev.trainedServiceAnimal ? ' · trained service animal' : ''}
              </div>
              <div style={{ color: 'var(--fg-muted)' }}>
                {`Revision ${gateRevisionNumber(ev)} · ${ev.evaluatedByDisplay ?? '—'} · ${fmtDate(ev.evaluatedAt)}`}
              </div>
            </div>
          </div>
          {!terminal && !showGate && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled={busy !== null} onClick={openGate}>
                {GATE.revise}
              </button>
            </div>
          )}
          {!terminal && gateForm}
        </div>
      ) : terminal ? null : (
        <div>
          {!showGate ? (
            <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled={busy !== null} onClick={openGate}>
              Record what we know…
            </button>
          ) : (
            gateForm
          )}
        </div>
      )}

      {item.infoRequests && item.infoRequests.length > 0 && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          More-info asked: {item.infoRequests.map((r) => `${r.gap} (${fmtDate(r.requestedAt)})`).join(' · ')}
        </div>
      )}

      {/* Decision. NOTE: a REVOKED lease-build item carries BOTH a decision
          (kept for audit) and a withdrawal — show both, decision first. */}
      {item.decision && (
        <div className="alert" data-tone={item.decision.outcome === 'approved' ? 'success' : 'danger'} style={{ marginTop: 'var(--space-3)' }}>
          <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>
            {awaitingApplication ? (
              <>
                {/* H1: approved before they applied — it applies when they do. */}
                <div className="alert-title">{`Approved — waiting for ${record.requester.name || 'the requester'} to apply`}</div>
                <div style={{ color: 'var(--fg-muted)' }}>
                  Approved {fmtDate(item.decision.decidedAt)} by {item.decision.decidedByDisplay}
                </div>
              </>
            ) : (
              <div className="alert-title">
                {item.decision.outcome === 'approved' ? 'Approved' : 'Denied'}
                {item.decision.origin === 'lease_build' ? ' at lease build' : ''}{' '}
                {fmtDate(item.decision.decidedAt)} by {item.decision.decidedByDisplay}
              </div>
            )}
            {item.decision.outcome === 'denied' && (
              <div>Ground: {item.decision.ground ?? '—'} · approver: {item.decision.denialApprovedBy ?? '—'}</div>
            )}
            {item.decision.outcome === 'approved' && item.status === 'approved' && (
              <div>
                {/* RRA v2.2: an approved animal request names the animals it covers. */}
                {covers && <div style={{ fontWeight: 'var(--weight-medium)' }}>{covers}</div>}
                {effectLine && <div>{effectLine}</div>}
                {addLine && <div style={{ marginTop: 'var(--space-1)' }}>{addLine}</div>}
                {(canChangeAnimals(record, item) || canRunApproveAgain(record, item)) && !picker && (
                  <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                    {canChangeAnimals(record, item) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => openPicker('change')}
                      >
                        Change animals
                      </button>
                    )}
                    {canRunApproveAgain(record, item) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => (runApproveAgainNeedsPicker(item) ? openPicker('approve') : approve())}
                      >
                        {busy === k('approve') ? 'Sending…' : 'Run Approve again'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* On an approved request the picker is "Change animals", or — for an approval that covers no animals
          yet — the re-run of Approve, which must carry the picks. */}
      {item.decision && picker !== null && (
        <ApprovePicker
          item={item}
          mode={picker}
          caseKind={record.kind}
          roster={roster}
          busy={busy === k(picker === 'approve' ? 'approve' : 'animals')}
          onConfirm={picker === 'approve' ? approve : changeAnimals}
          onCancel={closePicker}
        />
      )}
      {item.withdrawal && (
        <div className="alert" data-tone="warning" style={{ marginTop: 'var(--space-3)' }}>
          <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>
            <div className="alert-title">
              {item.decision?.origin === 'lease_build' ? 'Revoked' : 'Withdrawn'} {fmtDate(item.withdrawal.recordedAt)}
            </div>
            Evidence ({item.withdrawal.channel}): {item.withdrawal.note}
          </div>
        </div>
      )}
      {!item.decision && !item.withdrawal && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <div className="cluster" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn"
              data-variant="primary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => (needsAnimalPicker(record, item) ? openPicker('approve') : approve())}
            >
              {busy === k('approve') ? 'Approving…' : 'Approve'}
            </button>
            <button type="button" className="btn" data-variant="danger-ghost" data-size="sm" disabled={busy !== null} onClick={() => setShowDeny((v) => !v)}>
              Deny…
            </button>
            <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled={busy !== null} onClick={() => setShowMoreInfo((v) => !v)}>
              More info…
            </button>
            <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy !== null} onClick={() => setShowWithdraw((v) => !v)}>
              Withdraw…
            </button>
          </div>

          {picker === 'approve' && (
            <ApprovePicker
              item={item}
              mode="approve"
              caseKind={record.kind}
              roster={roster}
              busy={busy === k('approve')}
              onConfirm={approve}
              onCancel={closePicker}
            />
          )}

          {showDeny && (
            <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
                Denials require one of the configured approvers and a real permitted ground — never
                breed, size, species, or insurance.
              </div>
              <div className="field">
                <label className="label" htmlFor={`deny-${item.itemId}`}>Ground</label>
                <input id={`deny-${item.itemId}`} className="input" value={denyGround} onChange={(e) => setDenyGround(e.target.value)} />
              </div>
              <button
                type="button"
                className="btn"
                data-variant="danger"
                data-size="sm"
                disabled={busy !== null || denyGround.trim().length < 3}
                onClick={() => void run(k('deny'), () => denyRraItem(record.caseId, item.itemId, denyGround.trim()))}
              >
                {busy === k('deny') ? 'Recording…' : 'Deny (approver only)'}
              </button>
            </div>
          )}

          {showMoreInfo && (
            <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
              <div className="field">
                <label className="label" htmlFor={`mi-${item.itemId}`}>The specific gap (neutral wording)</label>
                <input id={`mi-${item.itemId}`} className="input" value={moreInfoGap} onChange={(e) => setMoreInfoGap(e.target.value)} placeholder="e.g. the named verifier has not responded — an alternate contact would help" />
              </div>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null || moreInfoGap.trim().length < 3}
                onClick={() => void run(k('more-info'), () => requestMoreInfoRraItem(record.caseId, item.itemId, moreInfoGap.trim()), 'Recorded — the request stays open.')}
              >
                {busy === k('more-info') ? 'Recording…' : 'Record more-info ask'}
              </button>
            </div>
          )}

          {showWithdraw && (
            <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
                Withdrawal needs recorded EVIDENCE the requester withdrew — silence never closes a
                request.
              </div>
              <div className="field">
                <label className="label" htmlFor={`wd-ch-${item.itemId}`}>How they withdrew</label>
                <select id={`wd-ch-${item.itemId}`} className="input" value={wdChannel} onChange={(e) => setWdChannel(e.target.value as RraWithdrawalChannel)}>
                  {WITHDRAW_CHANNELS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label" htmlFor={`wd-note-${item.itemId}`}>Evidence (one line)</label>
                <input id={`wd-note-${item.itemId}`} className="input" value={wdNote} onChange={(e) => setWdNote(e.target.value)} placeholder="e.g. emailed 7/14: found housing elsewhere" />
              </div>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null || wdNote.trim().length < 5}
                onClick={() => void run(k('withdraw'), () => withdrawRraItem(record.caseId, item.itemId, { channel: wdChannel, note: wdNote.trim() }))}
              >
                {busy === k('withdraw') ? 'Recording…' : 'Record withdrawal'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Verifications -------------------------------------------------------------

// RRA v2.1 (W3): the Verification panel — asks, answers, the front-line phone
// follow-up, records of replies and letters, and the case's staff documents. The
// pure logic (question set, answer bodies, the words on a card) lives in
// lib/rraVerificationUi.ts and is unit-tested there.

const NO_NOTE_BUTTON = 'Send the "wasn\'t able to confirm" note'; // DRAFT
const NO_NOTE_SENT = 'The note was sent to the requester.'; // DRAFT
const REPLY_NOT_USED = 'Noted, but not used: permission to contact had already been withdrawn.'; // DRAFT
const COPY_BUTTON = 'Their copy of the request'; // DRAFT
const COPY_UNAVAILABLE = "Their copy isn't available right now."; // DRAFT
const NO_PHONE_NOTE =
  'Verifier added. There is no number to call, so the requester is being asked for one.'; // DRAFT

/** Why a create-time front-line request did not go out (the verifier WAS added). */
function frontlineRefusalSentence(reason: string): string {
  switch (reason) {
    case 'test_recipient_blocked':
      return 'this is a test case and the front-line address is not an allowed test address';
    case 'send_failed':
      return 'the email could not be sent';
    default:
      return 'it could not be sent';
  }
}

/** The front-line phone follow-up on one ask: the timeline plus the actions staff take on it. */
function FrontlineBlock({
  record,
  v,
  busy,
  run,
}: {
  record: RraCaseView;
  v: RraVerificationView;
  busy: string | null;
  run: RunFn;
}) {
  if (!v.frontline) return null;
  const id = v.verificationId;
  const requesterName = record.requester.name?.trim() || 'the requester';
  const signerName = record.authorization.signedName?.trim() || requesterName;
  return (
    <FrontlineTimeline
      verification={v}
      requesterName={requesterName}
      signerName={signerName}
      busy={busy}
      onResume={() => void run(`v-resume:${id}`, () => resumeRraFrontline(record.caseId, id), 'Calls resumed.')}
      onConfirmWriting={(email) =>
        void run(
          `v-confirm-writing:${id}`,
          () => confirmRraWritingAddress(record.caseId, id, email),
          'Address confirmed. The request is on its way to them.',
        )
      }
      onConfirmPhone={(phone) =>
        void run(
          `v-confirm-phone:${id}`,
          () => confirmRraPhone(record.caseId, id, phone),
          'Number confirmed and calls resumed.',
        )
      }
      onRemoveNote={(callId) =>
        void run(`v-remove-note:${id}`, () => removeRraCallNote(record.caseId, id, callId), 'Note removed.')
      }
    />
  );
}

/** Letters, replies and documents staff added to the case (A3), with a presigned download. */
function StaffDocumentsList({
  record,
  busy,
  run,
  onRecordPermission,
}: {
  record: RraCaseView;
  busy: string | null;
  run: RunFn;
  onRecordPermission: (index: number) => void;
}) {
  const docs = record.staffDocuments ?? [];
  if (docs.length === 0) return null;
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-1)' }}>
        Letters and documents
      </div>
      <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
        {docs.map((d) => (
          <li key={d.index}>
            {DOCUMENT_KIND_WORD[d.kind]} · {d.filename}{' '}
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="xs"
              disabled={busy !== null}
              onClick={() =>
                void run(`doc:${d.index}`, async () => {
                  const { url } = await rraStaffDocumentUrl(record.caseId, d.index);
                  const a = document.createElement('a');
                  a.href = url;
                  a.target = '_blank';
                  a.rel = 'noopener';
                  a.click();
                })
              }
            >
              {busy === `doc:${d.index}` ? 'Opening…' : 'Download'}
            </button>
            {d.kind === 'signed_permission' && !d.permissionRecorded && (
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="xs"
                disabled={busy !== null}
                onClick={() => onRecordPermission(d.index)}
              >
                Record who it names
              </button>
            )}
            <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
              {' '}· added {fmtDate(d.addedAt)} by {d.addedByDisplay}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VerificationPanel({
  record,
  verifications,
  busy,
  run,
}: {
  record: RraCaseView;
  verifications: RraVerificationView[];
  busy: string | null;
  run: RunFn;
}) {
  // Only people a LIVE permission names (the server's contactableVerifiers) — never the legacy
  // aggregate list, which still names someone whose grant has expired.
  const authVerifiers = record.contactableVerifiers;
  const gapItems = record.items.filter((i) => i.evaluation?.outcome === 'gap' && !['approved', 'denied', 'withdrawn'].includes(i.status));
  const [showCreate, setShowCreate] = useState(false);
  const [showOral, setShowOral] = useState(false);
  const [verifierIdx, setVerifierIdx] = useState(0);
  // RRA v2: the outcome of a create-time "ask front line to call" (the verifier
  // WAS added either way, so a refusal is a warning here, not a thrown error).
  const [frontlineNote, setFrontlineNote] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);
  // RRA v2.1: which ask has "Record a reply or letter…" open; the case-level
  // "Add a letter or document…" form (existingIndex = a filed signed permission
  // that still needs its "who signed it" step); and a per-ask copy fallback line.
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [addDoc, setAddDoc] = useState<{ existingIndex?: number } | null>(null);
  const [copyMissing, setCopyMissing] = useState<string | null>(null);

  const canCreate = authVerifiers.length > 0 && gapItems.length > 0;
  const pickedVerifier = authVerifiers[verifierIdx];
  const pickedHasNoEmail = pickedVerifier !== undefined && !pickedVerifier.email;
  const pickedHasNoContact = pickedHasNoEmail && !pickedVerifier?.phone;
  const pickedNotice = contactNotice(pickedVerifier);
  const subjectName = subjectNameOf(record);

  /** Open the verifier's own copy in a new tab (the window opens first so a popup blocker allows it). */
  async function openCopy(verificationId: string) {
    setCopyMissing(null);
    const win = window.open('', '_blank');
    try {
      const blob = await rraVerifierCopyBlob(record.caseId, verificationId);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch {
      win?.close();
      setCopyMissing(verificationId);
    }
  }

  return (
    <div className="card">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-3)' }}>Verification</div>

      {verifications.length === 0 ? (
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
          No verifier contact yet.
        </div>
      ) : (
        <div className="stack-sm" style={{ marginBottom: 'var(--space-3)' }}>
          {verifications.map((v) => {
            const notesLine = requesterNotesLine(v.requesterNotes);
            return (
              <div key={v.verificationId} className="card" style={{ background: 'var(--surface-2, transparent)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)' }}>
                      {v.verifier.name ?? '—'}{v.verifier.role ? ` (${v.verifier.role})` : ''}
                    </div>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
                      {contactLine(v.verifier)} · asks: {v.gapDescription}
                    </div>
                  </div>
                  <span className="cluster" style={{ gap: 'var(--space-1)' }}>
                    {verificationPills(v).map((p) => (
                      <span key={p.text} className="pill" data-tone={p.tone} {...(p.title ? { title: p.title } : {})}>
                        {p.text}
                      </span>
                    ))}
                  </span>
                </div>
                {v.status !== 'cancelled' && (
                  <div style={{ marginTop: 'var(--space-1)' }}>
                    <button
                      type="button"
                      className="btn"
                      data-variant="ghost"
                      data-size="xs"
                      onClick={() => void openCopy(v.verificationId)}
                    >
                      {COPY_BUTTON}
                    </button>
                    {copyMissing === v.verificationId && (
                      <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}> {COPY_UNAVAILABLE}</span>
                    )}
                  </div>
                )}
                <FrontlineBlock record={record} v={v} busy={busy} run={run} />
                {v.response && (
                  <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>{responseLine(v.response)}</div>
                )}
                {v.receivedAfterWithdrawal && (
                  <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
                    {RECEIVED_AFTER_WITHDRAWAL}
                  </div>
                )}
                {notesLine && (
                  <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>{notesLine}</div>
                )}
                {(isOpenAsk(v) || canRecordReply(v) || answeredNo(v)) && (
                  <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                    {canSend(v) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => void run(`v-send:${v.verificationId}`, () => sendRraVerification(record.caseId, v.verificationId), 'Verification sent.')}
                      >
                        {busy === `v-send:${v.verificationId}` ? 'Sending…' : 'Send'}
                      </button>
                    )}
                    {isOpenAsk(v) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => void run(`v-frontline:${v.verificationId}`, () => triggerRraFrontline(record.caseId, v.verificationId), 'Front line has been emailed.')}
                      >
                        {busy === `v-frontline:${v.verificationId}` ? 'Emailing…' : 'Ask front line to call'}
                      </button>
                    )}
                    {canRecordReply(v) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => setReplyFor(replyFor === v.verificationId ? null : v.verificationId)}
                      >
                        Record a reply or letter…
                      </button>
                    )}
                    {isOpenAsk(v) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => void run(`v-cancel:${v.verificationId}`, () => cancelRraVerification(record.caseId, v.verificationId))}
                      >
                        Cancel ask
                      </button>
                    )}
                    {answeredNo(v) && (
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        disabled={busy !== null}
                        onClick={() => void run(`v-no-note:${v.verificationId}`, () => sendRraNoNote(record.caseId, v.verificationId), NO_NOTE_SENT)}
                      >
                        {busy === `v-no-note:${v.verificationId}` ? 'Sending…' : NO_NOTE_BUTTON}
                      </button>
                    )}
                    {isOpenAsk(v) && v.sentAt && (
                      <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
                        sent {fmtDate(v.sentAt)}{v.openedAt ? ` · opened ${fmtDate(v.openedAt)}` : ''}
                      </span>
                    )}
                  </div>
                )}
                {replyFor === v.verificationId && canRecordReply(v) && (
                  <RecordReplyForm
                    caseId={record.caseId}
                    verification={v}
                    gap={gapOfVerification(record.items, v.itemIds)}
                    subjectName={subjectName}
                    needObject={needObjectForItems(itemsOfVerification(record, v))}
                    onCancel={() => setReplyFor(null)}
                    onRecorded={async (res) => {
                      setReplyFor(null);
                      await run(`v-reply:${v.verificationId}`, async () => {}, res.usedAsVerification ? 'Reply recorded.' : REPLY_NOT_USED);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {frontlineNote && (
        <div className="alert" data-tone={frontlineNote.tone} role="status" style={{ marginBottom: 'var(--space-3)' }}>
          <div className="alert-body">{frontlineNote.text}</div>
        </div>
      )}

      <StaffDocumentsList
        record={record}
        busy={busy}
        run={run}
        onRecordPermission={(index) => setAddDoc({ existingIndex: index })}
      />
      {addDoc ? (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <AddDocumentForm
            caseId={record.caseId}
            {...(addDoc.existingIndex !== undefined ? { existingIndex: addDoc.existingIndex } : {})}
            onCancel={() => setAddDoc(null)}
            onFinished={async (notice) => {
              setAddDoc(null);
              await run('doc-added', async () => {}, notice);
            }}
          />
        </div>
      ) : (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={() => setAddDoc({})}>
            Add a letter or document…
          </button>
        </div>
      )}

      {!canCreate ? (
        <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
          {authVerifiers.length === 0
            ? 'Contacting a verifier needs a live permission to contact that names them: from the request form, the permission link (“Ask … who can confirm”), or a signed permission added under “Add a letter or document…”.'
            : 'No open item records a verification gap — if the disability and need are apparent, verification is skipped.'}
        </div>
      ) : !showCreate && !showOral ? (
        <div className="cluster" style={{ gap: 'var(--space-2)' }}>
          <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={() => setShowCreate(true)}>
            Contact a verifier…
          </button>
          <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={() => setShowOral(true)}>
            Record phone verification…
          </button>
        </div>
      ) : showOral ? (
        <OralVerificationForm
          record={record}
          gapItems={gapItems}
          authVerifiers={authVerifiers}
          busy={busy}
          run={run}
          onClose={() => setShowOral(false)}
        />
      ) : (
        <div className="card" style={{ background: 'var(--surface-2, transparent)' }}>
          <div className="field">
            <label className="label" htmlFor="rra-verifier-pick">Authorized verifier</label>
            <select id="rra-verifier-pick" className="input" value={verifierIdx} onChange={(e) => setVerifierIdx(Number(e.target.value))}>
              {authVerifiers.map((v, i) => (
                <option key={i} value={i}>
                  {v.name}{v.email ? ` — ${v.email}` : v.phone ? ' (no email on file)' : ' (no email or phone on file)'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
            Only people the requester named can be contacted. The email asks ONLY the recorded gap
            question(s) and attaches the verifier’s own copy of the signed request.
          </div>
          {pickedNotice && (
            <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
              {pickedNotice}
            </div>
          )}
          <div className="cluster" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn"
              data-variant="primary"
              data-size="sm"
              disabled={busy !== null || !authVerifiers[verifierIdx]}
              onClick={() => {
                const v = authVerifiers[verifierIdx]!;
                setFrontlineNote(null);
                void run(
                  'v-create',
                  async () => {
                    const res = await createRraVerification(record.caseId, {
                      itemIds: gapItems.map((i) => i.itemId),
                      verifier: { name: v.name ?? '', ...(v.email ? { email: v.email } : {}), ...(v.phone ? { phone: v.phone } : {}), ...(v.role ? { role: v.role } : {}) },
                    });
                    // No email on file → the server asked front line to phone instead
                    // (or, with no phone either, asked the requester for a number).
                    if (res.frontline) {
                      if (res.frontline.triggered) {
                        setFrontlineNote({ tone: 'success', text: 'Verifier added. Front line has been emailed to phone them.' });
                      } else if (res.frontline.reason === 'no_phone') {
                        setFrontlineNote({ tone: 'success', text: NO_PHONE_NOTE });
                      } else {
                        setFrontlineNote({
                          tone: 'warning',
                          text: `Verifier added, but front line was not emailed: ${frontlineRefusalSentence(res.frontline.reason)}. Use “Ask front line to call” to try again.`,
                        });
                      }
                    }
                  },
                  pickedHasNoEmail ? undefined : 'Verification created and sent.',
                );
                setShowCreate(false);
              }}
            >
              {busy === 'v-create'
                ? pickedHasNoContact ? 'Creating…' : pickedHasNoEmail ? 'Emailing front line…' : 'Sending…'
                : pickedHasNoContact ? 'Create' : pickedHasNoEmail ? 'Create + ask front line to call' : 'Create + send'}
            </button>
            <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Oral verification (phase 5) — the staff phone mini-form -------------------

/**
 * Records a PHONE verification: who spoke to whom, when, and the answers (RRA v2.1
 * E2): "Do you know {who} in a way that lets you answer?" Yes / No, and — with Yes —
 * each IN-GAP question Yes / No / They couldn't say. With No the rest is dropped
 * (and records no evidence). There is deliberately NO free-text field anywhere in
 * this form — the standing instruction is "do not record a diagnosis", and the
 * wire schema cannot carry one. The server drops out-of-gap answers and
 * re-enforces the authorization gate; the same core closes the emailed ask (A5).
 */
function OralVerificationForm({
  record,
  gapItems,
  authVerifiers,
  busy,
  run,
  onClose,
}: {
  record: RraCaseView;
  gapItems: RraItemView[];
  authVerifiers: Array<{ name?: string; email?: string; phone?: string; role?: string }>;
  busy: string | null;
  run: RunFn;
  onClose: () => void;
}) {
  const [verifierIdx, setVerifierIdx] = useState(0);
  const [spokeAt, setSpokeAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [answers, setAnswers] = useState<AnswerSelection>({ ...BLANK_ANSWERS });
  const [formError, setFormError] = useState<string | null>(null);

  // The union gap across the covered items — ONLY these questions render.
  const gap = gapOfItems(gapItems);
  const subjectName = subjectNameOf(record);
  const needObject = needObjectForItems(gapItems);

  return (
    <div className="card" style={{ background: 'var(--surface-2, transparent)' }}>
      <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
        Record a phone verification
      </div>
      <div className="alert" data-tone="warning" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="alert-body" style={{ fontSize: 'var(--text-xs)' }}>
          <strong>Do not record a diagnosis, condition name, or medical detail.</strong> Yes, no, or
          they couldn&rsquo;t say on the recorded gap question(s) is all that may be kept — there is
          no field for anything else, on purpose. Only a person named in a live permission to contact
          may be called.
        </div>
      </div>
      <div className="field">
        <label className="label" htmlFor="oral-verifier">Who you spoke with (named in a live permission)</label>
        <select id="oral-verifier" className="input" value={verifierIdx} onChange={(e) => setVerifierIdx(Number(e.target.value))}>
          {authVerifiers.map((v, i) => (
            <option key={i} value={i}>
              {v.name}{v.role ? ` (${v.role})` : ''}{v.phone ? ` — ${v.phone}` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="label" htmlFor="oral-date">When the call happened</label>
        <input id="oral-date" type="date" className="input" value={spokeAt} onChange={(e) => setSpokeAt(e.target.value)} />
      </div>
      <ProngAnswer
        legend={positionQuestion(subjectName)}
        name="oral-position"
        cantSay={false}
        value={answers.position}
        onChange={(v) => setAnswers({ ...answers, position: v === 'yes' ? 'yes' : 'no' })}
      />
      {answers.position === 'yes' && gap.askDisability && (
        <ProngAnswer
          legend={disabilityQuestion(subjectName)}
          name="oral-disability"
          value={answers.disability}
          onChange={(v) => setAnswers({ ...answers, disability: v })}
          above={<div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-1)' }}>{SPOKEN_DEFINITION}</div>}
        />
      )}
      {answers.position === 'yes' && gap.askNeed && (
        <ProngAnswer
          legend={needQuestion(subjectName, needObject)}
          name="oral-need"
          value={answers.need}
          onChange={(v) => setAnswers({ ...answers, need: v })}
        />
      )}
      {formError && (
        <div className="alert" data-tone="danger" role="alert" style={{ marginTop: 'var(--space-2)' }}>
          <div className="alert-body">{formError}</div>
        </div>
      )}
      <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
        <button
          type="button"
          className="btn"
          data-variant="primary"
          data-size="sm"
          disabled={busy !== null || !authVerifiers[verifierIdx]}
          onClick={() => {
            const v = authVerifiers[verifierIdx]!;
            const body = answerBody(answers, gap);
            if (!body) {
              setFormError(answerError(answers, gap));
              return;
            }
            setFormError(null);
            void run(
              'oral-record',
              () =>
                recordRraOralVerification(record.caseId, {
                  itemIds: gapItems.map((i) => i.itemId),
                  verifier: {
                    name: v.name ?? '',
                    ...(v.phone ? { phone: v.phone } : {}),
                    ...(v.email ? { email: v.email } : {}),
                    ...(v.role ? { role: v.role } : {}),
                  },
                  ...(spokeAt ? { spokeAt: receivedAtFromDate(spokeAt) } : {}),
                  ...body,
                }),
              'Phone verification recorded.',
            );
            onClose();
          }}
        >
          {busy === 'oral-record' ? 'Recording…' : 'Record verification'}
        </button>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// --- Case-level actions ---------------------------------------------------------

function CaseActionsPanel({
  record,
  busy,
  run,
  onDeleted,
}: {
  record: RraCaseView;
  busy: string | null;
  run: RunFn;
  onDeleted: () => void;
}) {
  const [wdChannel, setWdChannel] = useState<RraWithdrawalChannel>('phone');
  const [wdNote, setWdNote] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showRevoke, setShowRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  // RRA v2.1: withdrawal of permission to contact (A4) and the decline check (A9).
  const [showPermWithdraw, setShowPermWithdraw] = useState(false);
  const [showDeclineCheck, setShowDeclineCheck] = useState(false);
  const open = !['decided', 'withdrawn'].includes(record.status);
  // A memorialized build-time approval (decided lease_build case) can be undone.
  const canRevokeBuild = record.origin === 'lease_build' && record.status === 'decided';

  return (
    <div className="card">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-3)' }}>Case actions</div>
      <div className="cluster" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {open && (
          <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled={busy !== null} onClick={() => setShowWithdraw((v) => !v)}>
            Withdraw entire case…
          </button>
        )}
        {open && record.withdrawablePermission && (
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy !== null}
            onClick={() => setShowPermWithdraw((v) => !v)}
          >
            {`Record that ${record.withdrawablePermission.signerName} withdrew permission to contact`}
          </button>
        )}
        {record.canDeclineCheck && (
          <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled={busy !== null} onClick={() => setShowDeclineCheck((v) => !v)}>
            Record a decline check
          </button>
        )}
        {canRevokeBuild && (
          <button type="button" className="btn" data-variant="danger-ghost" data-size="sm" disabled={busy !== null} onClick={() => setShowRevoke((v) => !v)}>
            Revoke build-time approval…
          </button>
        )}
        {record.testMode && (
          <button
            type="button"
            className="btn"
            data-variant="danger-ghost"
            data-size="sm"
            disabled={deleteBusy}
            onClick={() => {
              setDeleteBusy(true);
              setDeleteError(null);
              deleteRraTestCase(record.caseId)
                .then(() => onDeleted())
                .catch((err) => setDeleteError(rraErrorMessage(err, 'Delete failed')))
                .finally(() => setDeleteBusy(false));
            }}
          >
            {deleteBusy ? 'Deleting…' : 'Delete test case'}
          </button>
        )}
      </div>
      {deleteError && (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>{deleteError}</div>
      )}
      {record.permissionWithdrawal && (
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
          {`Permission to contact withdrawn ${formatCalendarDate(record.permissionWithdrawal.receivedAt)}.`}
        </div>
      )}
      {showPermWithdraw && open && record.withdrawablePermission && (
        <WithdrawPermissionForm
          busy={busy !== null}
          running={busy === 'perm-withdraw'}
          onCancel={() => setShowPermWithdraw(false)}
          onSubmit={(body) =>
            void run(
              'perm-withdraw',
              async () => {
                const res = await withdrawRraPermission(record.caseId, body);
                setShowPermWithdraw(false);
                return res;
              },
              (result) => withdrawalNotice((result as Awaited<ReturnType<typeof withdrawRraPermission>> | undefined)?.notified),
            )
          }
        />
      )}
      {showDeclineCheck && record.canDeclineCheck && (
        <DeclineCheckForm
          busy={busy !== null}
          running={busy === 'decline-check'}
          onCancel={() => setShowDeclineCheck(false)}
          onSubmit={(body) =>
            void run('decline-check', async () => {
              const res = await recordRraDeclineCheck(record.caseId, body);
              setShowDeclineCheck(false);
              return res;
            })
          }
        />
      )}
      {record.declineChecks && record.declineChecks.length > 0 && (
        <ul className="stack-sm" style={{ listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0, fontSize: 'var(--text-sm)' }}>
          {record.declineChecks.map((c, i) => (
            <li key={`${c.recordedAt}-${i}`} style={{ color: 'var(--fg-muted)' }}>
              {`Decline check · ${fmtDate(c.recordedAt)} · ${c.recordedByDisplay}: ${c.ground}`}
            </li>
          ))}
        </ul>
      )}
      {showWithdraw && open && (
        <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
            Withdraws every open item. Requires recorded evidence the requester withdrew.
          </div>
          <div className="field">
            <label className="label" htmlFor="case-wd-ch">How they withdrew</label>
            <select id="case-wd-ch" className="input" value={wdChannel} onChange={(e) => setWdChannel(e.target.value as RraWithdrawalChannel)}>
              {WITHDRAW_CHANNELS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="case-wd-note">Evidence (one line)</label>
            <input id="case-wd-note" className="input" value={wdNote} onChange={(e) => setWdNote(e.target.value)} />
          </div>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy !== null || wdNote.trim().length < 5}
            onClick={() => void run('case-withdraw', () => withdrawRraCase(record.caseId, { channel: wdChannel, note: wdNote.trim() }))}
          >
            {busy === 'case-withdraw' ? 'Recording…' : 'Withdraw case'}
          </button>
        </div>
      )}
      {showRevoke && canRevokeBuild && (
        <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
            Undoes a build-time “waive fees” approval. The animal’s rent + deposit will no longer be
            waived — if a lease was already generated or sent for this household, regenerate/revise
            it so the charges match. A reason is required and recorded.
          </div>
          <div className="field">
            <label className="label" htmlFor="revoke-reason">Reason</label>
            <input id="revoke-reason" className="input" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
          </div>
          <button
            type="button"
            className="btn"
            data-variant="danger"
            data-size="sm"
            disabled={busy !== null || revokeReason.trim().length < 3}
            onClick={() => void run('revoke-build', () => revokeRraBuildApproval(record.caseId, revokeReason.trim()))}
          >
            {busy === 'revoke-build' ? 'Revoking…' : 'Revoke approval'}
          </button>
        </div>
      )}
    </div>
  );
}
